import '@babylonjs/loaders/glTF';
import './GLTFLoaderWorkerConfig';

import {
  AbstractMesh,
  AssetsManager,
  type ISceneLoaderAsyncResult,
  Scene,
  SceneLoader,
  Vector3,
} from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs';
import { Injectable } from '@modules/core';

import { GraphicAPI } from '../graphic';
import { PerformanceProfiler, RenderManager } from '../manager';
import { onAllModelLoaded } from '../observable';
import { buildSrcUrl, Profile } from '../util';

import { CallbackParameters, LoadOptions, LoadResult, SourceParameter } from './data';
import { EnvironmentLoader } from './EnvironmentLoader';
import { AsyncTextureLoader } from './extensions';
import { ZEDExtensionLoaderManager } from './extensions/ZEDExtensionLoaderManager';

export class GLTFLoader implements Injectable {
  static readonly INJECTABLE_TOKEN = Symbol.for('GLTFLoader');

  private readonly splitDot = '.';
  private readonly validateModelFormats = ['gltf', 'glb'];

  private readonly scene;
  private readonly extensionManager;
  private readonly asyncTextureLoader;
  private readonly renderManager;
  private readonly graphicAPI;
  private readonly environmentLoader;
  private readonly performanceProfiler;

  public readonly assetsManager;

  constructor({
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [ZEDExtensionLoaderManager.INJECTABLE_TOKEN]: extensionManager,
    [AsyncTextureLoader.INJECTABLE_TOKEN]: asyncTextureLoader,
    [RenderManager.INJECTABLE_TOKEN]: renderManager,
    [GraphicAPI.INJECTABLE_TOKEN]: graphicAPI,
    [EnvironmentLoader.INJECTABLE_TOKEN]: environmentLoader,
    [PerformanceProfiler.INJECTABLE_TOKEN]: performanceProfiler,
  }: {
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
    [ZEDExtensionLoaderManager.INJECTABLE_TOKEN]: ZEDExtensionLoaderManager;
    [AsyncTextureLoader.INJECTABLE_TOKEN]: AsyncTextureLoader;
    [RenderManager.INJECTABLE_TOKEN]: RenderManager;
    [GraphicAPI.INJECTABLE_TOKEN]: GraphicAPI;
    [EnvironmentLoader.INJECTABLE_TOKEN]: EnvironmentLoader;
    [PerformanceProfiler.INJECTABLE_TOKEN]: PerformanceProfiler;
  }) {
    this.scene = babylonjs.scene;
    this.assetsManager = new AssetsManager(this.scene);
    this.assetsManager.useDefaultLoadingScreen = false;
    this.extensionManager = extensionManager;
    this.asyncTextureLoader = asyncTextureLoader;
    this.renderManager = renderManager;
    this.graphicAPI = graphicAPI;
    this.environmentLoader = environmentLoader;
    this.performanceProfiler = performanceProfiler;
  }
  public dispose() {}

  private async loadModel<T extends { entityId: string }>(
    scene: Scene,
    param: SourceParameter,
    extensionData?: any,
    defaultEnabled: boolean = true,
    forceTransparencyMode: number | undefined = undefined
  ) {
    try {
      let src = param.src.trim();
      const extension = param.extension ?? src.split(this.splitDot).pop();
      if (extension && !this.validateModelFormats.includes(extension.toLowerCase())) {
        console.log(`.${extension} file format is not supported`);
        return;
      }

      const modelName = param.modelName;
      const pivotPosition = param.pivotPosition ?? Vector3.Zero();
      const position = param.position ?? Vector3.Zero();
      const rotationY = param.rotationY ?? 0;
      const scale = param.scale ?? Vector3.One();

      let model: ISceneLoaderAsyncResult;

      // * Component Loader
      const loaderInstance = this.extensionManager.getInstanceLoader(extensionData);
      model = await SceneLoader.ImportMeshAsync(
        '',
        buildSrcUrl(src, param.resourceServer),
        undefined,
        scene,
        undefined,
        `${this.splitDot}${extension}`
      );

      // * 입력한 modelName으로 rootTransform을 탐색
      let root = model.transformNodes.find((t) => t.name === modelName);
      if (!root) root = model.meshes.find((m) => m.name === modelName);

      if (root) {
        if (forceTransparencyMode !== undefined) {
          root.getChildMeshes().forEach((m) => {
            if (m.material) {
              m.material.transparencyMode = forceTransparencyMode;
            }
          });
        }

        // * Data에 의한 좌표/방향/크기 설정
        if (param.pivotBottom) {
          const boundingBox = root.getHierarchyBoundingVectors();
          const extendSize = boundingBox.max.subtract(boundingBox.min);

          root.setAbsolutePosition(pivotPosition.add(position.add(Vector3.Up().scale(extendSize.y * 0.5))));
        } else {
          root.setAbsolutePosition(pivotPosition.add(position));
        }

        root.rotation = new Vector3(0, rotationY, 0);
        if (!param.origibalScaling) root.scaling = scale;

        const parent = root.parent;
        if (param.parentTransform) {
          root.parent = param.parentTransform;
        } else {
          root.parent = null;
        }

        if (!defaultEnabled) root.setEnabled(defaultEnabled);
        if (parent) {
          parent.dispose(false, false);
        }
      } else {
        console.log(
          `입력한 modelName의 TransformNode or Mesh를 찾을 수 없어. root를 반환할 수 없습니다. modelName의: [${modelName}]`
        );
      }

      if (loaderInstance) {
        await loaderInstance.onComplete;
      }

      return {
        root: root,
        model: model,
        entities: loaderInstance ? loaderInstance.entities : [],
      } as LoadResult<T>;
    } catch (error) {
      console.log(error);
      return undefined;
    }
  }

  public async loadModels<T extends { entityId: string }>(
    scene: Scene,
    params: SourceParameter[],
    extensionData?: any,
    options?: Partial<LoadOptions>,
    callbacks?: Partial<CallbackParameters<T>>
  ) {
    Profile.start('Load Models');

    if (!this.extensionManager.registedLoaderCount) {
      console.log('gltf extra data로 component를 설정하려면 알맞은 loader를 등록하세요');
    }

    scene.blockfreeActiveMeshesAndRenderingGroups = true;

    const defaultEnabled = !(
      options &&
      ((options.defaultEnable !== undefined && !options.defaultEnable) ||
        (options.textureLoadedEnable !== undefined && options.textureLoadedEnable))
    );

    const tempResults = await Promise.all(
      params.map(async (param) => {
        return (
          (await this.loadModel<T>(scene, param, extensionData, defaultEnabled, options?.forceTransparencyMode)) || []
        );
      })
    );
    scene.blockfreeActiveMeshesAndRenderingGroups = false;

    const results = tempResults.flat();

    if (callbacks?.onModelLoaded) {
      callbacks.onModelLoaded(results);
    }

    Profile.end('Load Models');
    this.performanceProfiler.addCustom('Common', 'Load Models', Profile.get('Load Models'));

    if (options) {
      if (options.modelLoadedRender) {
        this.renderManager.renderUpdate();
      }

      if (options.autoLoadTexture) {
        await this.asyncTextureLoader.LoadAndApply();

        if (callbacks?.onTextureLoaded) {
          callbacks.onTextureLoaded(results);
        }
      }

      if (options.autoCompileShader) {
        await this.forceShaderCompilationAsync(
          results.flatMap((r) => {
            if (r && r.model) return r.model.meshes;
            else return [];
          })
        );

        if (callbacks?.onShaderCompiled) {
          callbacks.onShaderCompiled(results);
        }
      }

      if (options.textureLoadedEnable !== undefined && options.textureLoadedEnable) {
        for (const r of results) {
          r.model.transformNodes[0]?.setEnabled(true);
        }
      }

      if (options.finishedRender) {
        this.renderManager.renderUpdate();
      }

      if (options.finishedPrintProfile) {
        Profile.print();
        Profile.reset();
      }

      if (callbacks?.onFinished) {
        callbacks.onFinished(results);
      }
    }

    return results;
  }

  public loadGltf = async (rootPath: string, filePath: string, fileName: string) => {
    this.asyncTextureLoader.setRootImagePath(rootPath);

    // # Environment
    this.environmentLoader.applyEnvironment(this.scene, `assets/common/environment/default.env`);

    // # PostProcess
    await this.graphicAPI.applyPostProcess(`assets/common/postprocess/default.json`);

    // # Load Models
    await this.loadModels(
      this.scene,
      [
        {
          src: `${filePath}`,
          modelName: fileName,
        } as SourceParameter,
      ],
      undefined,
      {
        autoCompileShader: true,
        autoLoadTexture: true,
      },
      {
        onFinished: (_results) => {
          // # ModelLoad Callback
          if (onAllModelLoaded.hasObservers()) {
            onAllModelLoaded.notifyObservers(undefined);
          }

          Profile.print();
          Profile.reset();

          // # Finished Rendering
          this.renderManager.runRenderLoop(true);
        },
      }
    );
  };

  public async forceShaderCompilationAsync(meshes: AbstractMesh[]) {
    Profile.start('Shader Compile');

    await Promise.all(meshes.map((mesh) => mesh.material?.forceCompilationAsync(mesh)));

    Profile.end('Shader Compile');
    this.performanceProfiler.addCustom('Common', 'Shader Compile', Profile.get('Shader Compile'));
  }
}
